import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { EhrChip } from "@/components/chat/SidebarChat/DocLink/EhrChip";
import { ehrUrlFor, isEhrRegistryName } from "./ehrUrl";
import { ReasoningInfoPopover } from "./SourceGroupChip";

/**
 * The document a value's evidence comes from: the
 * /documents extension badge + filename (+ page), clickable to open the
 * preview. The source's reasoning collapses into a small ⓘ beside it — a
 * click-opened popover, not a hover tooltip, since a tablet has to be able to
 * read it. EHR evidence names a registry, not a document: Ehitisregister
 * renders as its link chip, any other name as plain text.
 *
 * Shared by the inspector's SOURCE EVIDENCE section and the review dialog so
 * both surfaces present provenance identically. Callers hide the surrounding
 * section entirely when `fieldHasSourceEvidence` is false.
 */
export function EvidenceSourceCard(props: {
  /** The primary source is EHR — the card names a registry, not a document. */
  isEhr: boolean;
  filename: string | null;
  canPreview: boolean;
  pageLabel: string | null;
  reasoning: string | null;
  registryCode: string | null;
  onDocPreview: (filename: string) => void;
}) {
  const { isEhr, filename, canPreview, pageLabel, reasoning, registryCode, onDocPreview } = props;
  const trimmedReasoning = reasoning?.trim() || null;
  if (!isEhr && !filename) {
    // No file to name and no registry either, so there is nothing for an ⓘ to
    // sit beside — a lone 16px icon reads as an empty row. The reasoning is the
    // whole card here, so render it as text.
    if (!trimmedReasoning) return null;
    return (
      <p className="mt-3 whitespace-pre-wrap px-1 py-1 text-xs leading-5 text-muted-foreground">
        {trimmedReasoning}
      </p>
    );
  }
  return (
    <div className="mt-3 flex items-center gap-2 rounded-lg px-1 py-1">
      {canPreview && filename ? (
        <button
          type="button"
          onClick={() => onDocPreview(filename)}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left transition-colors hover:bg-accent/50"
        >
          <FileTypeIcon filename={filename} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-foreground">{filename}</span>
            {pageLabel ? (
              <span className="block text-xs text-muted-foreground">{pageLabel}</span>
            ) : null}
          </span>
        </button>
      ) : (
        // Nothing openable, so the name has to speak for itself — but only
        // Ehitisregister earns the chip and its deep link. Any other name (a
        // second registry named in the free-text source system, or a document
        // that no longer resolves) is plain text: linking it to EHR would credit
        // a registry that never reported the value. `isEhrRegistryName` treats
        // the empty/absent name as Ehitisregister, which is the normal EHR row.
        // The page reference still applies either way.
        <span className="flex min-w-0 flex-1 items-center gap-3">
          {isEhr && isEhrRegistryName(filename) ? (
            <EhrChip url={ehrUrlFor(registryCode)} className="h-6" />
          ) : (
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
              {filename}
            </span>
          )}
          {pageLabel ? (
            <span className="shrink-0 text-xs text-muted-foreground">{pageLabel}</span>
          ) : null}
        </span>
      )}
      {trimmedReasoning ? <ReasoningInfoPopover reasoning={trimmedReasoning} /> : null}
    </div>
  );
}
